import React from "react";
import { FiDownload, FiMail } from "react-icons/fi";
import "../../styles/ExecutivePrograms.css";

export default function ExecutivePrograms() {
  const programs = [
    {
      id: "01",
      title: "Security Sector Governance & Reform (SSR) in Africa",
      alignment: ["Governance", "Peace & Security"],
      focus: [
        "Security Sector Reform (SSR)",
        "Democratic governance",
        "Anti-corruption in security",
        "State / non-state coordination",
      ],
      output: "National Security Reform Roadmap",
      icon: "https://via.placeholder.com/56?text=SSR",
    },
    {
      id: "02",
      title: "Digital Governance & Public Sector Transformation",
      alignment: ["Digital Transformation", "Public Governance"],
      focus: [
        "e-Government",
        "Digital sovereignty",
        "Public service transformation",
        "Data governance",
      ],
      output: "Digital Transformation Strategy",
      icon: "https://via.placeholder.com/56?text=Digital",
    },
    {
      id: "03",
      title: "Cybersecurity, Cybercrime & Digital Justice",
      alignment: ["Cybersecurity", "Rule of Law"],
      focus: [
        "Cyber threats",
        "Cybercrime",
        "Digital evidence",
        "International cooperation",
      ],
      output: "Cyber Incident Response Report",
      icon: "https://via.placeholder.com/56?text=Cyber",
    },
    {
      id: "04",
      title: "Artificial Intelligence & Security Governance",
      alignment: ["Emerging Technologies", "AI Governance"],
      focus: [
        "AI governance",
        "Ethics and bias",
        "Intelligent surveillance",
        "Algorithmic decision-making",
      ],
      output: "National AI Governance Framework",
      icon: "https://via.placeholder.com/56?text=AI",
    },
    {
      id: "05",
      title: "Crisis Management, Resilience & Risk Governance",
      alignment: ["Resilience", "Disaster Risk Management"],
      focus: [
        "Hybrid crises",
        "Multi-actor coordination",
        "Human security",
        "Territorial resilience",
      ],
      output: "Crisis Management Plan",
      icon: "https://via.placeholder.com/56?text=Crisis",
    },
    {
      id: "06",
      title: "People-Centered Security & Local Governance",
      alignment: ["Inclusive Governance", "Community Engagement"],
      focus: [
        "Local security",
        "Citizen participation",
        "Non-state actors",
        "Territorial governance",
      ],
      output: "Local Security Strategy",
      icon: "https://via.placeholder.com/56?text=People",
    },
  ];

  return (
    <div className="executive-programs-container">
      {/* Hero Section */}
      <header className="executive-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-badge">
            <span>GSIDA Executive Programs · IAMAR Institute · Agadir</span>
          </div>
          <h1 className="header-title">
            High-Level Executive Training in Security Governance and Digital Transformation
          </h1>
          <p className="header-subtitle">
            A premium executive training platform designed to position IAMAR and GSIDA as a continental reference in policy-oriented capacity-building, aligned with leading international standards.
          </p>
          <div className="header-actions">
            <button className="btn btn-primary">
              <FiDownload size={18} />
              Download Brochure
            </button>
            <button className="btn btn-secondary">
              <FiMail size={18} />
              Request Information
            </button>
          </div>

          <div className="header-stats">
            <div className="stat-item">
              <div className="stat-value">6</div>
              <div className="stat-label">Executive Programs</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">2 Days</div>
              <div className="stat-label">Per Program</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">Policy Lab</div>
              <div className="stat-label">Practical Outputs</div>
            </div>
          </div>
        </div>

        <div className="header-sidebar">
          <div className="sidebar-box">
            <div className="sidebar-label">Program Structure</div>
            <div className="sidebar-title">Concept → Application → Production</div>
            <div className="sidebar-badge">ONU / Genève Style</div>
            <div className="sidebar-items">
              <div className="sidebar-item">
                <strong>Day 1</strong>
                <p>Conceptual frameworks, international standards, comparative benchmarks</p>
              </div>
              <div className="sidebar-item">
                <strong>Day 2</strong>
                <p>Case studies, simulations, workshops, strategic deliverables</p>
              </div>
              <div className="sidebar-item">
                <strong>Audience</strong>
                <p>Senior officials, professionals, institutions, international partners</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Overview Section */}
      <section className="overview-section">
        <div className="overview-grid">
          <div className="overview-card">
            <h3>Capacity Building</h3>
            <p>Designed as executive-level capacity-building programs for public institutions, strategic sectors, and emerging African governance ecosystems.</p>
          </div>
          <div className="overview-card">
            <h3>Policy-Oriented Learning</h3>
            <p>Each program is structured around practical policy outputs, turning training into an immediate institutional resource.</p>
          </div>
          <div className="overview-card">
            <h3>Operational Impact</h3>
            <p>A strong emphasis on simulation, applied analysis, and strategic planning ensures direct real-world relevance.</p>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="programs-section">
        <div className="section-intro">
          <div className="section-header-v2">
            <span className="accent-line"></span>
            <h2>Executive Programs</h2>
          </div>
          <p className="section-subtitle">
            A structured executive portfolio aligned with international governance priorities
          </p>
          <p className="section-description">
            Each executive program is presented as a self-contained institutional offer, combining thematic alignment, strategic focus, and a concrete policy deliverable.
          </p>
        </div>

        <div className="programs-grid">
          {programs.map((program) => (
            <article key={program.id} className="program-card">
              <div className="program-header">
                <img src={program.icon} alt={program.title} className="program-icon" />
                <div className="program-id">PROGRAM {program.id}</div>
              </div>

              <h3 className="program-title">{program.title}</h3>

              <div className="program-alignment">
                <div className="alignment-label">Alignment</div>
                <div className="alignment-tags">
                  {program.alignment.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="program-focus">
                <div className="focus-label">Strategic Focus</div>
                <ul className="focus-list">
                  {program.focus.map((item) => (
                    <li key={item}>
                      <span className="focus-bullet"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="program-output">
                <div className="output-label">Key Output</div>
                <div className="output-text">{program.output}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="why-matters-section">
        <div className="why-matters-content">
          <div className="why-matters-left">
            <div className="section-header-v2">
              <span className="accent-line"></span>
              <h2>Executive education designed as a policy lab, not a conventional course catalogue</h2>
            </div>
            <p>
              The GSIDA Executive Programs are designed to turn knowledge into actionable institutional responses. They combine international standards, African strategic priorities, and operational deliverables.
            </p>
          </div>

          <div className="why-matters-grid">
            <div className="why-card">
              <h4>International Standards</h4>
              <p>Structured in a format compatible with high-level executive learning methodologies.</p>
            </div>
            <div className="why-card">
              <h4>Africa-Focused</h4>
              <p>Directly linked to security, governance, and digital transformation priorities across the continent.</p>
            </div>
            <div className="why-card">
              <h4>Practical Outputs</h4>
              <p>Each participant leaves with a policy document, roadmap, or strategic response tool.</p>
            </div>
            <div className="why-card">
              <h4>Institutional Relevance</h4>
              <p>Suitable for ministries, public agencies, universities, think tanks, and international partners.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Target & CTA Section */}
      <section className="target-cta-section">
        <div className="target-box">
          <h3>Target Audience</h3>
          <ul className="target-list">
            <li>Senior government officials</li>
            <li>Security sector professionals</li>
            <li>Policy advisors and public managers</li>
            <li>International organizations</li>
            <li>Researchers and executive learners</li>
          </ul>
        </div>

        <div className="cta-box">
          <h3>Strategic Statement</h3>
          <blockquote>
            "GSIDA Executive Programs are designed as policy labs to transform knowledge into actionable governance solutions."
          </blockquote>
          <p>
            This positioning enables IAMAR to present the Executive Programs not as a traditional training offer, but as a continental governance and innovation platform with high operational relevance.
          </p>
          <div className="cta-buttons">
            <button className="btn btn-primary">Explore Programs</button>
            <button className="btn btn-outline">Contact IAMAR</button>
          </div>
        </div>
      </section>
    </div>
  );
}
